import cx from 'classnames'
import React from 'react'
import { useMediaQuery } from 'react-responsive'
import { Required } from 'utility-types'
import { Alert, Button, Typography } from '@commutifi-fe/ui'
import { ACCOUNT_PENDING_EMAIL_VERIFICATION_ERROR_CODE } from '@commutifi/constants/Errors'
import { useDispatch, useSelector } from 'react-redux'
import { noop } from 'lodash'
import { FormattedMessage, useIntl } from 'locales/index'
import { colors, screenSizes, spacing } from 'constants/global'
import { useLoginWithRedirect } from 'shared/hooks/oauth'
import { LoginBody, SSOLoginState } from 'api/modules/authentication'
import { useSubdomain } from 'api/modules/subdomains'
import { authActions, authSelectors } from 'store/modules/auth'
import { useNotifications } from '@commutifi-fe/commutifi-specific/hooks'
import { STAY_LOGGED_IN_STORAGE_ID } from 'constants/settings'
import { StringSerializer, useQueryParams } from 'shared/hooks/urlQueryParams'
import { wrapActionInPromise } from 'utils/helpers'
import { getCommutifiErrorCode } from 'utils/index'
import { AuthLayout } from 'views/Anonymous/views/Auth/layout/AuthLayout'
import * as localStorage from 'utils/storage'
import { SSOLogin } from './components/SsoLogin'
import { LoginForm, LoginFormProps } from './LoginForm'
import { Account } from '@commutifi-fe/interfaces'
import { usePostEmailVerificationEmail } from 'api/modules/accounts'

type LoginRes = { redirectPath?: string } & Account
export interface LoginProps extends Required<Omit<LoginFormProps, 'onLogin'>, 'onForgotPassword'> {
  // Usually used together: canCreateAccount and onCreateAccount callback
  canCreateAccount?: boolean
  onCreateAccount?: React.MouseEventHandler
  onLoginSuccess?: (res: LoginRes) => void
  /**
   * Used for SSO login redirect callback
   */
  oauthState: Pick<SSOLoginState, 'shortlinkId' | 'targetUrl'>
}

const commonAlertProps = {
  style: { borderRadius: '4px', marginBottom: spacing.medium2 },
  className: 'u-margin-bottom--large'
}

export function Login({
  oauthState,
  canCreateAccount,
  onLoginSuccess,
  onForgotPassword,
  onCreateAccount,
  ...rest
}: LoginProps) {
  const isXSmall = useMediaQuery({ maxWidth: screenSizes.xsMaxWidth })
  const intl = useIntl()
  const dispatch = useDispatch()
  const login = React.useCallback(
    (...args: Parameters<typeof authActions.loginRequest<LoginRes>>) => dispatch(authActions.loginRequest(...args)),
    [dispatch]
  )

  const loginError = useSelector(authSelectors.getError)
  const commutifiNotification = useNotifications()
  const [queryParams, setQueryParams] = useQueryParams({ error: StringSerializer })
  const handleResetQueryParam = () => setQueryParams({ error: undefined })
  const [emailNotVerified, setEmailNotVerified] = React.useState<string | null>(null)
  const { data } = useSubdomain()

  const {
    loading: extractingLoginUrl,
    error: loginRedirectError,
    loginWithRedirect
  } = useLoginWithRedirect<SSOLoginState>()

  const handleLoginWithIdentityProvider = () =>
    data &&
    loginWithRedirect(data.id, {
      ...oauthState,
      identityProviderId: data.id,
      subdomain: data.subdomain
    })

  const handleLogin = async (values: LoginBody) => {
    try {
      const res = await wrapActionInPromise(login, values, {
        stayLoggedIn: localStorage.get(STAY_LOGGED_IN_STORAGE_ID)
      })
      onLoginSuccess?.(res)
    } catch (error) {
      const errorCode = getCommutifiErrorCode(error)
      if (errorCode && errorCode === ACCOUNT_PENDING_EMAIL_VERIFICATION_ERROR_CODE && values.email) {
        setEmailNotVerified(values.email.trim())
      } else {
        setEmailNotVerified(null)
        handleResetQueryParam()
      }
    }
  }

  const handleForgotPassword: React.MouseEventHandler = (e) => onForgotPassword(e)
  const handleCreateAccount: React.MouseEventHandler = (e) => onCreateAccount && onCreateAccount(e)

  const { mutateAsync: sendEmail } = usePostEmailVerificationEmail()
  const handleResendEmail = async () => {
    try {
      if (!emailNotVerified) {
        throw new Error('Missing email to send verification to')
      }

      await sendEmail({ email: emailNotVerified })
      commutifiNotification.success(intl.formatMessage({ id: 'registration.emailVerification.send.success' }))
    } catch (error) {
      commutifiNotification.error({
        description: intl.formatMessage({ id: 'registration.errors.emailVerification.resend' })
      })
    }
  }

  const getQueryStringError = React.useCallback(() => {
    if (queryParams.error) {
      const messageId = `error.${queryParams.error}` as
        | 'error.duplicate'
        | 'error.unauthorized'
        | 'error.expiredToken'
        | 'error.loginFailure'
        | 'error.accountDeactivated'
        | 'error.tryAgain'
      const translatedError = intl.formatMessage({ id: messageId, defaultMessage: 'Oops something happened' })

      if (translatedError !== messageId) {
        return translatedError
      }
      return intl.formatMessage({ id: 'error.sorry' })
    }
    return undefined
  }, [intl, queryParams.error])

  return (
    <AuthLayout
      title={<FormattedMessage id="auth.login.title" />}
      logoURLs={data?.customBranding?.logoUrl}
      message={
        emailNotVerified ? (
          <Alert
            {...commonAlertProps}
            type="warning"
            style={{
              height: 'fit-content',
              backgroundColor: colors.accentOrange100
            }}
            showIcon
            message={<FormattedMessage id="registration.verification.error.title" />}
            description={
              <span>
                <FormattedMessage id="registration.verification.error.pendingVerification" />{' '}
                <Button
                  type="link"
                  style={{ padding: 0, height: 'auto' }}
                  size="small"
                  className="u-color--neutral-600"
                  onClick={handleResendEmail}
                >
                  <Typography.Text underline strong="semi-bold">
                    <FormattedMessage id="global.sendEmail" />
                  </Typography.Text>
                </Button>
              </span>
            }
          />
        ) : queryParams.error || loginError ? (
          <Alert
            {...commonAlertProps}
            type="error"
            message={
              queryParams.error
                ? getQueryStringError()
                : intl.formatMessage({
                    id: loginError.messageKey || 'error.sorry'
                  })
            }
            closable
            onClose={queryParams.error ? handleResetQueryParam : noop}
          />
        ) : null
      }
    >
      {data?.subdomain ? (
        <SSOLogin
          providerName={data?.providerName || ''}
          onSubmit={handleLoginWithIdentityProvider}
          isLoading={extractingLoginUrl}
        >
          {loginRedirectError ? (
            <Alert
              type="error"
              showIcon
              closable
              style={{ borderRadius: '4px', marginBottom: spacing.large }}
              message={<FormattedMessage id="error.sorry" />}
              description={<FormattedMessage id="error.500.text" />}
            />
          ) : null}
        </SSOLogin>
      ) : (
        <LoginForm
          onLogin={handleLogin}
          onForgotPassword={handleForgotPassword}
          extraActions={
            canCreateAccount ? (
              <Button
                ghost
                onClick={handleCreateAccount}
                block={isXSmall}
                className={cx(!isXSmall && 'u-margin-left--medium-2')}
              >
                <FormattedMessage id="registration.buttons.submit" />
              </Button>
            ) : undefined
          }
          {...rest}
        />
      )}
    </AuthLayout>
  )
}

export default Login
